import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Loader2, CalendarCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useBooking } from "@/context/BookingContext";
import { getCookieConsent } from "./CookieConsent";

const TEAL = "#22D3C5";
const TEAL_INK = "#04211c";

const APP_PREFIXES = [
  "/login", "/register", "/kom-i-gang", "/glemt-adgangskode", "/nulstil-adgangskode",
  "/onboarding", "/betaling", "/dashboard", "/ugens-forslag", "/idebank",
  "/brandfortaelling", "/linkedin", "/facebook", "/nyhedsbrev", "/presse",
  "/kampagner", "/leadgenerator", "/projekter", "/vidensbank", "/indstillinger",
  "/priser", "/admin",
];

const SUGGESTIONS = [
  "Hvad er Aicom AI?",
  "Hvad kan I hjælpe os med?",
  "Hvad koster det?",
  "Kan jeg prøve det gratis?",
];

const QUICK_LINKS = [
  { label: "Se løsninger", to: "/#losninger" },
  { label: "Gratis guides", to: "/inspiration" },
  { label: "Opret gratis konto", to: "/kom-i-gang" },
];

function sessionId() {
  try {
    let s = localStorage.getItem("aicom_site_session");
    if (!s) {
      s = (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2));
      localStorage.setItem("aicom_site_session", s);
    }
    return s;
  } catch {
    return "anon-" + Math.random().toString(16).slice(2);
  }
}

export default function SiteChatWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openBooking } = useBooking();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sid = useRef(sessionId());
  const [wave, setWave] = useState(false);
  const [greet, setGreet] = useState(false);
  const [consented, setConsented] = useState(() => !!getCookieConsent());
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false
  );

  const isAppRoute = APP_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  // Track viewport (mobile) + cookie consent so chat only shows on mobile after consent
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    const onConsent = () => setConsented(true);
    window.addEventListener("cookie-consent-updated", onConsent);
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("cookie-consent-updated", onConsent);
    };
  }, []);

  // Wave + greeting bubble the first time it's seen (once per browser session)
  useEffect(() => {
    if (isAppRoute) return;
    let seen = false;
    try { seen = sessionStorage.getItem("aicom_chat_greeted") === "1"; } catch { /* ignore */ }
    if (seen) return;
    const t0 = setTimeout(() => {
      setWave(true);
      setGreet(true);
      try { sessionStorage.setItem("aicom_chat_greeted", "1"); } catch { /* ignore */ }
    }, 1200);
    const t1 = setTimeout(() => setWave(false), 4200);
    const t2 = setTimeout(() => setGreet(false), 7000);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
  }, [isAppRoute]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open && !loaded) {
      api.get(`/site/chat/${sid.current}`)
        .then(({ data }) => setMessages(data.messages || []))
        .catch(() => {})
        .finally(() => { setLoaded(true); scrollToBottom(); });
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open, loaded, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, sending, scrollToBottom]);

  if (isAppRoute) return null;
  if (isMobile && !consented) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/site/chat", { session_id: sid.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(apiError(err));
      setMessages((m) => [...m, { role: "assistant", content: "Beklager – jeg kunne ikke svare lige nu. Prøv igen, eller book et møde, så vender vi tilbage." }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const goTo = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      <AnimatePresence>
        {greet && !open && (
          <motion.button
            data-testid="site-chat-greeting"
            onClick={() => { setGreet(false); setOpen(true); }}
            initial={{ opacity: 0, x: 16, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 16, scale: 0.9 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-[1.6rem] sm:bottom-[5.6rem] right-24 z-[9999] max-w-[220px] rounded-2xl rounded-br-sm bg-white border border-[#DDD8D0] px-4 py-3 text-left shadow-xl"
          >
            <p className="text-sm text-[#111111] font-medium leading-snug">Hej! 👋</p>
            <p className="text-xs text-[#5B5B5B] leading-snug mt-0.5">Har du spørgsmål? Jeg hjælper gerne.</p>
          </motion.button>
        )}
      </AnimatePresence>

      <button
        data-testid="site-chat-toggle"
        onClick={() => { setOpen((v) => !v); setGreet(false); }}
        aria-label="Åbn Aicom-hjælperen"
        className="fixed bottom-4 sm:bottom-20 right-5 z-[9999] h-14 w-14 rounded-full flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform duration-200"
        style={{ backgroundColor: TEAL, color: TEAL_INK }}
      >
        {!open && wave && (
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: TEAL }}
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: 0, scale: 1.7 }}
            transition={{ duration: 1.4, repeat: 2, repeatDelay: 0.2, ease: "easeOut" }}
          />
        )}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="h-6 w-6" strokeWidth={2} />
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              style={{ display: "inline-flex" }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                y: wave ? [0, -5, 0, -3, 0] : 0,
                scale: wave ? [1, 1.12, 1, 1.08, 1] : 1,
                rotate: wave ? [0, -8, 8, -5, 0] : 0,
              }}
              exit={{ opacity: 0 }}
              transition={wave
                ? { duration: 1.1, repeat: 2, repeatDelay: 0.5, ease: "easeInOut" }
                : { duration: 0.2 }}
            >
              <MessageCircle className="h-6 w-6" strokeWidth={2} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="site-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="fixed bottom-24 sm:bottom-40 right-5 z-[9999] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-8rem)] sm:max-h-[calc(100vh-11rem)] rounded-2xl border border-[#DDD8D0] bg-white shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#DDD8D0] shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: TEAL }}>
                  <Sparkles className="h-4 w-4" style={{ color: TEAL_INK }} strokeWidth={2} />
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-medium text-[#111111]">Aicom-hjælperen</p>
                  <p className="text-xs text-[#5B5B5B]">Hvad kan vi hjælpe dig med?</p>
                </div>
              </div>
              <button data-testid="site-chat-close" onClick={() => setOpen(false)} title="Luk"
                className="h-8 w-8 rounded-md flex items-center justify-center text-[#5B5B5B] hover:text-[#111111] hover:bg-black/[0.05] transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="site-chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center pt-4">
                  <div className="h-12 w-12 rounded-xl bg-[#F4F1EB] border border-[#DDD8D0] flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="h-6 w-6" style={{ color: TEAL }} strokeWidth={1.75} />
                  </div>
                  <p className="text-sm text-[#111111] font-medium">Hej! Hvad kan jeg hjælpe med?</p>
                  <p className="text-xs text-[#5B5B5B] mt-1 mb-4">Spørg mig om Aicoms løsninger, AI-platformen eller priser.</p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="site-chat-suggestion" onClick={() => send(s)}
                        className="text-left text-sm px-3 py-2 rounded-lg border border-[#DDD8D0] text-[#5B5B5B] hover:text-[#111111] hover:border-[#22D3C5] transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`site-chat-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#22D3C5] text-[#04211c] rounded-br-sm"
                        : "bg-[#F4F1EB] text-[#111111] border border-[#DDD8D0] rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start" data-testid="site-chat-typing">
                  <div className="bg-[#F4F1EB] text-[#5B5B5B] border border-[#DDD8D0] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Skriver...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="px-3 pt-2 shrink-0">
              <button data-testid="site-chat-book" onClick={() => { setOpen(false); openBooking(); }}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
                style={{ backgroundColor: TEAL, color: TEAL_INK }}>
                <CalendarCheck className="h-4 w-4" /> Book et gratis 15-min møde
              </button>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {QUICK_LINKS.map((l) => (
                  <button key={l.to} data-testid="site-chat-quicklink" onClick={() => goTo(l.to)}
                    className="text-xs px-2.5 py-1.5 rounded-full border border-[#DDD8D0] text-[#5B5B5B] hover:text-[#111111] hover:border-[#22D3C5] transition-colors">
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="p-3 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="site-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  maxLength={1000}
                  placeholder="Skriv dit spørgsmål..."
                  className="flex-1 resize-none max-h-28 rounded-xl border border-[#DDD8D0] bg-[#FAF9F6] px-3 py-2.5 text-sm text-[#111111] placeholder:text-[#5B5B5B] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#22D3C5]"
                />
                <button
                  data-testid="site-chat-send"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: TEAL, color: TEAL_INK }}
                >
                  <Send className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              <p className="mt-2 text-center text-[11px] text-[#5B5B5B]">
                Fik du ikke svar, som du ønskede? Skriv direkte til{" "}
                <a href="mailto:hello@aicom.dk" data-testid="site-chat-email-link" className="hover:underline" style={{ color: TEAL }}>hello@aicom.dk</a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
